/** Spin result generation — client-side RNG, swappable to server */

import { WinEvaluator } from './WinEvaluator';
import { Logger } from '../utils/Logger';

const logger = new Logger('SpinResultGenerator');

export interface WinResult {
  symbol: string;
  streakLength: number;
  ways: number;
  coins: number;
  positions: [number, number][]; // [reel, row] pairs
}

export interface FeatureTrigger {
  type: 'FREE_GAMES' | 'RUSH_FEVER' | 'JACKPOT_PICK';
  params: Record<string, unknown>;
}

export interface SpinResult {
  stopPositions: number[];
  grid: string[][];
  wins: WinResult[];
  totalWin: number;
  scatterCount: number;
  rushCount: number;
  features: FeatureTrigger[];
  rushSymbolValues?: number[];
}

export interface SpinContext {
  isBaseGame: boolean;
  freeSpinIndex?: number;
  totalBet: number;
}

export interface ISpinResultGenerator {
  generate(
    reelStrips: readonly (readonly string[])[],
    context: SpinContext
  ): SpinResult;
}

/** Client-side spin result generator using local RNG */
export class ClientSpinResultGenerator implements ISpinResultGenerator {

  generate(
    reelStrips: readonly (readonly string[])[],
    context: SpinContext
  ): SpinResult {
    // 1. Generate random stop positions
    const stopPositions = reelStrips.map((strip) =>
      Math.floor(Math.random() * strip.length)
    );

    // 2. Build visible grid from stop positions
    const grid = this._buildGrid(reelStrips, stopPositions);

    // 3. Evaluate wins
    const wins = WinEvaluator.evaluate(grid, {
      isBaseGame: context.isBaseGame,
      totalBet: context.totalBet,
    });

    // 4. Calculate total monetary win
    const totalWin = WinEvaluator.calculateTotalWin(wins, context.totalBet);

    // 5. Count special symbols
    const scatterCount = WinEvaluator.countScatters(grid);
    const rushCount = WinEvaluator.countRush(grid);

    // 6. Determine feature triggers
    const features: FeatureTrigger[] = [];
    if (scatterCount >= 3) {
      features.push({
        type: 'FREE_GAMES',
        params: { scatterCount, freeSpins: 12 },
      });
    }
    // Rush fever triggers when enough Rush symbols appear (future implementation)

    logger.info(
      `Spin result: stops=[${stopPositions.join(',')}], ` +
      `wins=${wins.length}, totalWin=${totalWin.toFixed(4)}, ` +
      `scatters=${scatterCount}, rush=${rushCount}`
    );

    return {
      stopPositions,
      grid,
      wins,
      totalWin,
      scatterCount,
      rushCount,
      features,
    };
  }

  /**
   * Build the visible 3-row grid from reel strips and stop positions.
   * Returns grid[reelIndex][rowIndex] = symbolId
   */
  private _buildGrid(
    reelStrips: readonly (readonly string[])[],
    stopPositions: number[]
  ): string[][] {
    return stopPositions.map((pos, reelIdx) => {
      const strip = reelStrips[reelIdx];
      const symbols: string[] = [];
      for (let row = 0; row < 3; row++) {
        symbols.push(strip[(pos + row) % strip.length]);
      }
      return symbols;
    });
  }
}
